import {
  useAppDispatch,
  useAppSelector,
} from "@/app/lib/features/redux/redux.hook";

import { useCallback, useMemo, useRef } from "react";

import {
  getCollections as getCollectionsThunk,
  getCollection as getCollectionThunk,
  getCollectionFormData as getCollectionFormDataThunk,
  createCollection as createCollectionThunk,
  updateCollection as updateCollectionThunk,
  deleteCollection as deleteCollectionThunk,
  getFollowedCollections as getFollowedCollectionsThunk,
  getCollectionsSummary as getCollectionsSummaryThunk,
  addItemToCollection as addItemToCollectionThunk,
  removeItemFromCollection as removeItemFromCollectionThunk,
  toggleFollowCollection as toggleFollowCollectionThunk,
} from "./collection.thunk";

import {
  resetCollections,
  resetSelectedCollection,
  resetFormData,
} from "./collection.slice";

import { isLoading, isError, isSuccess } from "@/app/utils/asyncStatus";

import type {
  CreateCollectionRequest,
  UpdateCollectionRequest,
  AddItemToCollectionRequest,
  RemoveItemFromCollectionRequest,
  CreateCollectionResponse,
} from "./collection.types";

import type { FilterRequest } from "../filters/filters.types";

const useCollection = () => {
  const dispatch = useAppDispatch();
  const lastFiltersRef = useRef<Partial<FilterRequest> | undefined>(undefined);

  const state = useAppSelector((state) => state.collection);

  /* ================= LIST ================= */

  const fetchCollections = useCallback(
    async (params?: { search?: string }) => {
      const res = await dispatch(getCollectionsThunk(params));
      return res.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const fetchFollowedCollections = useCallback(
    async (params?: { search?: string }) => {
      const res = await dispatch(getFollowedCollectionsThunk(params));
      return res.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const fetchSummary = useCallback(async () => {
    const res = await dispatch(getCollectionsSummaryThunk());
    return res.meta.requestStatus === "fulfilled";
  }, [dispatch]);

  /* ================= SINGLE ================= */

  const fetchCollection = useCallback(
    async (idOrUuid: string | number, filters?: Partial<FilterRequest>) => {
      lastFiltersRef.current = filters;

      const res = await dispatch(
        getCollectionThunk({
          idOrUuid,
          page: 1,
          filters,
        }),
      );

      return res.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const fetchMoreCollectionItems = useCallback(() => {
    if (!state.selectedCollection || !state.selectedCollectionPagination)
      return;

    const { current_page, last_page } = state.selectedCollectionPagination;

    if (current_page >= last_page) return;

    dispatch(
      getCollectionThunk({
        idOrUuid: state.selectedCollection.collectionUUID,
        page: current_page + 1,
        filters: lastFiltersRef.current,
      }),
    );
  }, [dispatch, state.selectedCollection, state.selectedCollectionPagination]);

  /* ================= FORM ================= */

  const fetchFormData = useCallback(
    async (id?: number) => {
      const res = await dispatch(getCollectionFormDataThunk(id));
      return res.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  /* ================= MUTATIONS ================= */

  const createCollection = useCallback(
    async (data: CreateCollectionRequest) => {
      const res = await dispatch(createCollectionThunk(data));

      if (res.meta.requestStatus === "fulfilled") {
        const payload = res.payload as CreateCollectionResponse;
        return payload.data.id;
      }

      return null;
    },
    [dispatch],
  );

  const updateCollection = useCallback(
    async (collectionId: number, data: UpdateCollectionRequest) => {
      const res = await dispatch(updateCollectionThunk({ collectionId, data }));
      return res.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const deleteCollection = useCallback(
    async (collectionId: number) => {
      const res = await dispatch(deleteCollectionThunk(collectionId));
      return res.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  /* ================= FOLLOW ================= */

  const toggleFollow = useCallback(
    async (collectionId: number) => {
      const res = await dispatch(toggleFollowCollectionThunk(collectionId));
      return res.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  /* ================= ITEMS ================= */

  const addItem = useCallback(
    async (collectionId: number, data: AddItemToCollectionRequest) => {
      const res = await dispatch(
        addItemToCollectionThunk({ collectionId, data }),
      );
      const fulfilled = res.meta.requestStatus === "fulfilled";
      const payload = fulfilled
        ? (res.payload as { status?: boolean; message?: string } | undefined)
        : undefined;
      const rejectedMessage = !fulfilled ? (res.payload as string | undefined) : undefined;
      const success = fulfilled && (payload?.status ?? true);
      const message = payload?.message ?? rejectedMessage ?? undefined;
      return { success, message };
    },
    [dispatch],
  );

  const removeItem = useCallback(
    async (collectionId: number, data: RemoveItemFromCollectionRequest) => {
      const res = await dispatch(
        removeItemFromCollectionThunk({ collectionId, data }),
      );
      return res.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  /* ================= RESET ================= */

  const clearCollections = useCallback(() => {
    dispatch(resetCollections());
  }, [dispatch]);

  const clearSelectedCollection = useCallback(() => {
    dispatch(resetSelectedCollection());
  }, [dispatch]);

  const clearFormData = useCallback(() => {
    dispatch(resetFormData());
  }, [dispatch]);

  const hasMoreCollectionItems = useMemo(() => {
    if (!state.selectedCollectionPagination) return false;
    return (
      state.selectedCollectionPagination.current_page <
      state.selectedCollectionPagination.last_page
    );
  }, [state.selectedCollectionPagination]);

  return {
    ...state,

    // Convenience alias: state.data → collections
    collections: state.data,

    isCollectionsLoading: isLoading(state.status),
    isCollectionsSuccess: isSuccess(state.status),
    isSelectedCollectionLoading: isLoading(state.selectedCollectionStatus),
    isSelectedCollectionSuccess: isSuccess(state.selectedCollectionStatus),
    isSelectedCollectionError: isError(state.selectedCollectionStatus),
    isFormDataLoading: isLoading(state.formDataStatus),
    isFollowedCollectionsLoading: isLoading(state.followedCollectionsStatus),
    isSummaryLoading: isLoading(state.summaryStatus),

    hasMoreCollectionItems,

    fetchCollections,
    fetchCollection,
    fetchMoreCollectionItems,
    fetchFormData,
    createCollection,
    updateCollection,
    deleteCollection,
    fetchFollowedCollections,
    fetchSummary,
    addItem,
    removeItem,
    toggleFollow,

    clearCollections,
    clearSelectedCollection,
    clearFormData,
  };
};

export default useCollection;
